import { MpinState } from '../../utils/mpinGate';

/**
 * Everything that differs between the three MPIN variants is data here —
 * the page renders fields in a loop and runs one generic submit.
 */

export type MpinField =
    'pin' | 'confirmPin' | 'oldPin' | 'newPin' | 'confirmNewPin';

export type MpinValues = Partial<Record<MpinField, string>>;
export type MpinErrors = Partial<Record<MpinField | 'form', string>>;

export interface MpinFieldConfig {
    key: MpinField;
    label: string;
}

export interface MpinVariantConfig {
    title: (firstName: string) => string;
    subtitle: string;
    /** Verify shows the avatar + greeting header. */
    showGreeting: boolean;
    fields: MpinFieldConfig[];
    /** Client-side pre-flight: these two fields must match before the API call. */
    confirmPair?: { source: MpinField; confirm: MpinField };
    /** Render the "Forgot MPIN?" link under this field. */
    forgotAfterField?: MpinField;
    cta: string;
    successTitle: string | null;
}

export const MPIN_VARIANTS: Record<
    Exclude<MpinState, 'NOT_APPLICABLE'>,
    MpinVariantConfig
> = {
    SETUP_REQUIRED: {
        title: () => 'Set up your PIN',
        subtitle:
            "Create a 6-digit PIN. You'll enter it after every OTP login to keep your account secure.",
        showGreeting: false,
        fields: [
            { key: 'pin', label: 'Enter PIN' },
            { key: 'confirmPin', label: 'Re-enter PIN' },
        ],
        confirmPair: { source: 'pin', confirm: 'confirmPin' },
        cta: 'Set PIN',
        successTitle: 'PIN set',
    },
    VERIFY_REQUIRED: {
        title: (firstName) => (firstName ? `Hi, ${firstName}` : 'Welcome back'),
        subtitle: 'Enter your PIN to continue',
        showGreeting: true,
        fields: [{ key: 'pin', label: 'Enter your PIN' }],
        forgotAfterField: 'pin',
        cta: 'Confirm PIN',
        successTitle: null,
    },
    CHANGE_REQUIRED: {
        title: () => 'Update your PIN',
        subtitle:
            'Your PIN needs to be updated. Enter your current PIN and choose a new one.',
        showGreeting: false,
        fields: [
            { key: 'oldPin', label: 'Current PIN' },
            { key: 'newPin', label: 'New PIN' },
            { key: 'confirmNewPin', label: 'Re-enter new PIN' },
        ],
        confirmPair: { source: 'newPin', confirm: 'confirmNewPin' },
        forgotAfterField: 'oldPin',
        cta: 'Update PIN',
        successTitle: 'PIN updated',
    },
};

export const CONFIRM_MISMATCH_MESSAGE = "PINs don't match. Please re-enter.";
export const FALLBACK_ERROR_MESSAGE = 'Something went wrong. Please try again.';

/**
 * One outcome shape for all three endpoints: which field the (VERBATIM)
 * server message anchors under, the counters when present, and which
 * inputs to wipe.
 */
export interface MpinSubmitOutcome {
    ok: boolean;
    field?: MpinField | 'form';
    message?: string;
    attemptsRemaining?: number | null;
    blockedForSeconds?: number | null;
    clearFields?: MpinField[];
}

/**
 * Normalizes the three backend response shapes into MpinSubmitOutcome.
 * Both success and error callbacks receive the full envelope
 * `{status:{code,message}, response:{...}}` from the API middleware.
 *
 * - setup:  {success, message}                    — rule text, no counters
 * - verify: {valid, attemptsRemaining, blockedForSeconds, message}
 * - change: {success, attemptsRemaining, blockedForSeconds, message}
 *
 * Anchoring policy: only errors the CLIENT can attribute to one input are
 * field-anchored (the confirm-mismatch pre-flight, and verify's single
 * field). Server messages on setup/change (rule violations, wrong current
 * MPIN) render in the common form-level area — the payload doesn't say
 * which row they belong to (counters ride along even on rule violations),
 * so pinning them under a row mislabels the error.
 */
export const normalizeMpinOutcome = (
    state: Exclude<MpinState, 'NOT_APPLICABLE'>,
    envelope: any,
): MpinSubmitOutcome => {
    const body = envelope?.response ?? {};
    const bodyCode = envelope?.status?.code;

    if (bodyCode && bodyCode !== 200) {
        return {
            ok: false,
            field: 'form',
            message: envelope?.status?.message ?? FALLBACK_ERROR_MESSAGE,
            clearFields: [],
        };
    }

    if (state === 'SETUP_REQUIRED') {
        if (body?.success) return { ok: true };
        // Rule violations (weak / history): common error, both rows wiped
        // so the user starts the pair fresh.
        return {
            ok: false,
            field: 'form',
            message: body?.message ?? FALLBACK_ERROR_MESSAGE,
            clearFields: ['pin', 'confirmPin'],
        };
    }

    if (state === 'VERIFY_REQUIRED') {
        if (body?.valid) return { ok: true };
        return {
            ok: false,
            field: 'pin',
            message: body?.message ?? FALLBACK_ERROR_MESSAGE,
            attemptsRemaining: body?.attemptsRemaining ?? null,
            blockedForSeconds: body?.blockedForSeconds ?? null,
            clearFields: ['pin'],
        };
    }

    // CHANGE_REQUIRED — wrong current MPIN and new-MPIN rule violations
    // both arrive as {success:false, message, counters}; the message text
    // is the only field signal, so it renders in the common area and all
    // three rows reset. Counters still pass through (a wrong current MPIN
    // burns a verify attempt).
    if (body?.success) return { ok: true };
    return {
        ok: false,
        field: 'form',
        message: body?.message ?? FALLBACK_ERROR_MESSAGE,
        attemptsRemaining: body?.attemptsRemaining ?? null,
        blockedForSeconds: body?.blockedForSeconds ?? null,
        clearFields: ['oldPin', 'newPin', 'confirmNewPin'],
    };
};

/** "Try again in 14:59" — lockout remainder, always under an hour. */
export const formatMmSs = (totalSeconds: number): string => {
    const clamped = Math.max(0, totalSeconds);
    const minutes = Math.floor(clamped / 60);
    const seconds = clamped % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

/** "Ramesh Kulkarni" → "Ramesh" / "RK" for the verify greeting. */
export const firstNameOf = (name?: string | null): string =>
    (name ?? '').trim().split(/\s+/).filter(Boolean)[0] ?? '';

export const initialsOf = (name?: string | null): string =>
    (name ?? '')
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('');
